import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-notes-preview/artifacts/qfix72o2_Lumero_Logo_transparent_with_name-removebg-preview.png";

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
  { label: "Why us", href: "#why-lumeroflow" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "header-glass bg-[#FAF9F6]/80 border-b border-[#1A1B1A]/[0.05] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[72px]">
        {/* Logo */}
        <a
          href="/"
          data-testid="logo-link"
          className="flex items-center z-10"
        >
          <img
            src={LOGO_URL}
            alt="LumeroFlow"
            className="h-8 md:h-9 w-auto"
            data-testid="header-logo-img"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-[#4A4B4A] hover:text-[#1A1B1A] transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-5" data-testid="desktop-cta-group">
          <a
            href="/app/login"
            data-testid="header-login-link"
            className="text-sm font-medium text-[#4A4B4A] hover:text-[#1A1B1A] transition-colors duration-200"
          >
            Log in
          </a>
          <a
            href="https://www.lumeroflow.co.uk"
            data-testid="header-demo-btn"
            className="text-sm font-medium text-[#1A1B1A] border border-[#1A1B1A]/15 hover:border-[#1A1B1A]/30 px-5 py-2 rounded-full transition-all duration-300"
          >
            Book a demo
          </a>
          <a
            href="/app/signup"
            data-testid="header-signup-btn"
            className="text-sm font-medium text-white bg-[#205B5A] hover:bg-[#174544] px-5 py-2 rounded-full transition-all duration-300 shadow-sm"
          >
            Sign up now
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden z-10 p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden fixed inset-0 top-0 bg-[#FAF9F6] z-40 flex flex-col pt-24 px-8"
        >
          <nav className="flex flex-col gap-6">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-2xl font-medium text-[#1A1B1A] hover:text-[#205B5A] transition-colors"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="mt-10 flex flex-col gap-4">
            <a
              href="/app/signup"
              data-testid="mobile-signup-btn"
              className="bg-[#205B5A] text-white text-center px-8 py-4 rounded-full font-medium text-base hover:bg-[#174544] transition-colors"
            >
              Sign up now
            </a>
            <a
              href="https://www.lumeroflow.co.uk"
              data-testid="mobile-demo-btn"
              className="border border-[#1A1B1A]/20 text-[#1A1B1A] text-center px-8 py-4 rounded-full font-medium text-base hover:border-[#1A1B1A]/40 transition-colors"
            >
              Book a demo
            </a>
            <a
              href="/app/login"
              data-testid="mobile-login-link"
              className="text-sm text-center text-[#205B5A] mt-2"
            >
              Already have an account? Log in
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
